import { queryOptions, skipToken, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { fetchUserPreferences } from "../APIs/UserPreferences/GetUserPreferences";
import {
  USER_PREFERENCES_QUERY_ROOT,
  userPreferencesQueryKey
} from "../config/queryConfig";
import { useAppSelector } from "../store/hooks";
import { selectCollinsUser } from "../store/slices/sessionSlice";
import {
  DEFAULT_USER_PREFERENCES,
  type UserPreferences
} from "../types/userPreferences";
import {
  mergeWithDefaults,
  reportPreferencesLoadError
} from "../utils/userPreferences";

export const userPreferencesQueryOptions = (userId: string | undefined) =>
  queryOptions({
    queryKey: userPreferencesQueryKey(userId),
    queryFn: userId
      ? async () => {
          try {
            const preferences = await fetchUserPreferences(userId);
            return preferences
              ? mergeWithDefaults(preferences)
              : DEFAULT_USER_PREFERENCES;
          } catch (error) {
            reportPreferencesLoadError(error);
            throw error;
          }
        }
      : skipToken,
    staleTime: Infinity, // Preserve one automatic fetch per user.
    retry: false, // fetchUserPreferences already retries after refreshing a 401.
    refetchOnReconnect: false, // Keep the old fetch-on-mount behaviour offline.
    refetchOnWindowFocus: false, // Avoid fetching preferences on focus.
    networkMode: "always" // Let the plain fetch run while the browser reports offline.
  });

export const useUserPreferencesQuery = () => {
  const userId = useAppSelector(selectCollinsUser)?._id;
  const query = useQuery(userPreferencesQueryOptions(userId));
  const { refetch } = query;

  const loadPreferences = useCallback(async () => {
    if (!userId) return;
    await refetch().catch(() => undefined);
  }, [refetch, userId]);

  return {
    preferences: query.data ?? DEFAULT_USER_PREFERENCES,
    hasLoadError: query.isError,
    /**
     * True once the initial fetch for the current user has settled (resolved
     * or failed). Consumers that write derived data must wait for this,
     * otherwise they race the initial load and can persist defaults over
     * server state.
     */
    hasLoadedPreferences: query.isFetched,
    // Initial auto-load never shows the preferences page skeleton.
    isLoading: query.isRefetching,
    loadPreferences,
    userId
  };
};

export const useUserPreference = <Value>(
  select: (preferences: UserPreferences) => Value
): Value => {
  const userId = useAppSelector(selectCollinsUser)?._id;
  const selectWithDefaults = useCallback(
    (preferences: UserPreferences | undefined) =>
      select(preferences ?? DEFAULT_USER_PREFERENCES),
    [select]
  );
  const query = useQuery({
    ...userPreferencesQueryOptions(userId),
    select: selectWithDefaults
  });

  return query.data ?? select(DEFAULT_USER_PREFERENCES);
};

export { USER_PREFERENCES_QUERY_ROOT };
